import { Link } from "react-router-dom";
import { ArrowUpRight, HeartHandshake, Users, Stethoscope, BookOpen, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1770954098074-da95ccdf33e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHdhdGVyY29sb3IlMjBuZXV0cmFsJTIwY2FsbXxlbnwwfHx8fDE3NzczNDE5MDl8MA&ixlib=rb-4.1.0&q=85";
const WHAT_IMG =
  "https://images.unsplash.com/photo-1758216434916-0819f9cd3a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHdhdGVyY29sb3IlMjBuZXV0cmFsJTIwY2FsbXxlbnwwfHx8fDE3NzczNDE5MDl8MA&ixlib=rb-4.1.0&q=85";

const audiences = [
  {
    icon: HeartHandshake,
    title: "Patients",
    body: "If the ground keeps moving under you — this is a place to slow down and feel understood.",
    testid: "audience-patients",
  },
  {
    icon: Users,
    title: "Families",
    body: "Plain-language explanations for the people who love someone living with FND.",
    testid: "audience-families",
  },
  {
    icon: Stethoscope,
    title: "Clinicians",
    body: "A patient perspective to sit alongside your clinical reading.",
    testid: "audience-clinicians",
  },
  {
    icon: BookOpen,
    title: "Educators",
    body: "Plain, human language for classrooms, carers, and curious minds.",
    testid: "audience-educators",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 relative animate-fade-up">
              <span
                data-testid="hero-eyebrow"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-8"
              >
                <span className="w-6 h-px bg-terracotta" />
                An introduction
              </span>
              <h1
                data-testid="hero-title"
                className="font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight text-balance"
              >
                It’s real.
                <br />
                You’re not
                <span className="italic text-terracotta"> imagining </span>
                this.
              </h1>
              <p
                data-testid="hero-sub"
                className="mt-8 text-lg lg:text-xl text-charcoal-light dark:text-bone/65 leading-relaxed max-w-xl text-pretty"
              >
                Functional Neurological Disorder is often invisible, and hard
                to explain. This is a place to start making sense of what’s
                happening in your body.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/what-is-fnd"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-terracotta text-bone text-sm font-medium tracking-wide hover:bg-terracotta-hover transition-all duration-500"
                >
                  Start with the basics
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.5}
                    className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
                <Link
                  to="/symptoms"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-charcoal/20 text-charcoal dark:text-bone text-sm font-medium tracking-wide hover:border-charcoal/50 hover:bg-bone-100 transition-all duration-500"
                >
                  Explore symptoms
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-fade-in">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[2rem] bg-bone-100">
                <img
                  src={HERO_IMG}
                  alt="Abstract soft watercolor hues representing calm"
                  data-testid="hero-image"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-bone/30 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 sm:-left-10 max-w-[18rem] bg-bone/90 backdrop-blur-md rounded-2xl p-6 border border-bone-200 dark:border-charcoal-light/25 shadow-[0_10px_40px_-20px_rgba(44,48,46,0.25)]">
                <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.22em] text-moss mb-2">
                  <Sparkles size={14} strokeWidth={1.5} />
                  From lived experience
                </div>
                <p className="font-serif text-lg text-charcoal dark:text-bone leading-snug italic">
                  “No two people live the same FND. This is mine, softly offered.”
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative marquee-like banner */}
        <div
          data-testid="hero-marquee"
          className="border-y border-bone-200 dark:border-charcoal-light/25 bg-bone-100/50 dark:bg-charcoal-light/10"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-xs uppercase tracking-[0.32em] text-charcoal-light">
            <span>Invisible</span>
            <span className="text-terracotta">·</span>
            <span>Misunderstood</span>
            <span className="text-terracotta">·</span>
            <span>Deeply Real</span>
            <span className="text-terracotta">·</span>
            <span>Not Your Fault</span>
            <span className="text-terracotta">·</span>
            <span>You Are Not Alone</span>
          </div>
        </div>
      </section>

      {/* WHAT IS FND intro */}
      <section className="py-20 lg:py-28" data-testid="home-what-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[2rem]">
                <img
                  src={WHAT_IMG}
                  alt="Soft flowing fabric in neutral tones"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
                <span className="w-6 h-px bg-moss" />
                What is FND?
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal dark:text-bone leading-[1.08] tracking-tight text-balance">
                When your brain sends the{" "}
                <span className="italic text-terracotta">wrong signals</span>
                {" "}to your body.
              </h2>
              <div className="mt-8 space-y-5 text-lg text-charcoal-light dark:text-bone/65 leading-relaxed max-w-2xl text-pretty">
                <p>
                  Functional Neurological Disorder — <em>FND</em> — is a
                  condition where the brain misfires its connective signals.
                  In plain language: the wiring is fine, but the messages it
                  sends to the body arrive scrambled.
                </p>
                <p>
                  That can look like non-epileptic seizures, temporary loss
                  of vision or hearing, tremors, or limbs that suddenly refuse
                  to cooperate. It is not imagined. It is not a choice. And it
                  is shaped uniquely by every person who lives with it.
                </p>
              </div>
              <Link
                to="/what-is-fnd"
                data-testid="home-what-link"
                className="mt-10 group inline-flex items-center gap-2 text-sm font-medium text-charcoal dark:text-bone border-b border-charcoal/30 pb-1 hover:text-terracotta hover:border-terracotta transition-colors"
              >
                Read the full explanation
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.5}
                  className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-20 lg:py-28 bg-bone-100/60 dark:bg-charcoal-light/10" data-testid="home-audience-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
              <span className="w-6 h-px bg-terracotta" />
              Who this is for
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal dark:text-bone leading-[1.1] tracking-tight text-balance">
              Written for anyone learning how to support — themselves or someone they love.
            </h2>
            <p className="mt-6 text-base text-charcoal-light dark:text-bone/65 leading-relaxed max-w-2xl">
              I’m not a medical professional. I’m someone living with FND,
              and these words are what I wish I’d found when I started looking.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {audiences.map((a, i) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.title}
                  data-testid={a.testid}
                  className="group relative bg-bone dark:bg-charcoal-light/10 rounded-3xl border border-bone-200 dark:border-charcoal-light/25 p-8 hover-lift transition-colors duration-500 hover:border-terracotta/40"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-terracotta/10 text-terracotta mb-6 transition-all duration-500 group-hover:bg-terracotta group-hover:text-bone">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl text-charcoal dark:text-bone mb-3 tracking-tight">
                    {a.title}
                  </h3>
                  <p className="text-sm text-charcoal-light dark:text-bone/65 leading-relaxed">
                    {a.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUICK LINKS */}
      <section className="py-20 lg:py-28" data-testid="home-quicklinks">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Link
              to="/what-is-fnd"
              data-testid="quicklink-what"
              className="group relative overflow-hidden rounded-[2rem] bg-charcoal text-bone p-10 lg:p-14 hover-lift"
            >
              <span className="text-xs uppercase tracking-[0.28em] text-bone/60">01 · Begin here</span>
              <h3 className="mt-6 font-serif text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                What is FND,<br />really?
              </h3>
              <p className="mt-5 text-bone/70 max-w-sm leading-relaxed">
                A simple definition, the common symptoms, and why FND behaves
                so differently from conditions you can “see” on a scan.
              </p>
              <span className="mt-10 inline-flex items-center gap-2 text-sm font-medium border-b border-bone/40 pb-1 group-hover:border-terracotta group-hover:text-terracotta transition-colors">
                Read the explainer
                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
              <div className="pointer-events-none absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-terracotta/30 blur-3xl" />
            </Link>

            <Link
              to="/symptoms"
              data-testid="quicklink-symptoms"
              className="group relative overflow-hidden rounded-[2rem] bg-terracotta text-bone p-10 lg:p-14 hover-lift"
            >
              <span className="text-xs uppercase tracking-[0.28em] text-bone/70">02 · Explore</span>
              <h3 className="mt-6 font-serif text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                Symptoms<br />& types
              </h3>
              <p className="mt-5 text-bone/80 max-w-sm leading-relaxed">
                Tap through motor, sensory, cognitive and functional-seizure
                symptoms — grouped softly so you can take it at your own pace.
              </p>
              <span className="mt-10 inline-flex items-center gap-2 text-sm font-medium border-b border-bone/50 pb-1 group-hover:border-bone transition-colors">
                Open the explorer
                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
              <div className="pointer-events-none absolute -left-16 -top-16 w-64 h-64 rounded-full bg-bone/20 blur-3xl" />
            </Link>
          </div>
        </div>
      </section>

      {/* LATEST / NOTE */}
      <section className="pb-24 lg:pb-32" data-testid="home-note">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-[2rem] border border-bone-200 dark:border-charcoal-light/25 bg-bone-100/60 dark:bg-charcoal-light/10 p-10 lg:p-14">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
              <span className="w-6 h-px bg-moss" />
              Latest
            </span>
            <p className="font-serif text-2xl lg:text-3xl text-charcoal dark:text-bone leading-snug italic text-balance">
              “If you take one thing from this site: FND is invisible, but it
              is not imagined. Your body’s signals are crossed — and that’s a
              real, neurological thing.”
            </p>
            <p className="mt-6 text-sm uppercase tracking-[0.22em] text-charcoal-light">
              — A note from the author
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
